import { useState } from "react";

import { useUser } from "MVVM/Hooks/useUser";
import { useLinkedAccounts } from "MVVM/Hooks/useLinkedAccounts";
import { useSteambot } from "MVVM/Hooks/useSteambot";
import { URLS } from "MVVM/Utilities/Maps";

const DISMISS_KEY = "setup-checklist-dismissed";

export type SetupItemKey = "link" | "steambot" | "sharecodes" | "autocapture";

export type SetupItem = {
  key: SetupItemKey;
  label: string;
  benefit: string;
  action: string;
  done: boolean;
  // Why the step can't be started yet. Shown greyed out in its place.
  blockedBy?: string;
  // Absent means the item opens the clip settings modal instead of routing.
  to?: string;
};

export type SetupInput = {
  hasGameLinked: boolean;
  // Display names of linked game accounts, e.g. ["Steam", "Riot"].
  linkedPlatforms?: string[];
  hasSteamLinked: boolean;
  steambot?: {
    loading: boolean;
    eligible: boolean;
    enrolled: boolean;
    capacityAvailable: boolean;
  };
  hasSharecodes: boolean;
  autoclip: boolean;
};

const linkedLabel = (platforms: string[] = []) => {
  if (!platforms.length) return "Account linked";
  if (platforms.length === 1) return `${platforms[0]} account linked`;
  const last = platforms[platforms.length - 1];
  return `${platforms.slice(0, -1).join(", ")} and ${last} accounts linked`;
};

// Never hidden: a greyed step with a reason tells users what comes next.
// Mirrors kanye's steambotStatus: every plan is eligible once Steam is linked.
// Share codes aren't required.
const steambotBlocker = ({ hasSteamLinked, steambot: bot }: SetupInput) => {
  if (bot?.enrolled) return undefined;
  if (!hasSteamLinked) return "Link Steam first";
  if (!bot || bot.loading || !bot.eligible) return undefined;
  if (!bot.capacityAvailable) return "All bots are full, check back soon";
  return undefined;
};

export const buildSetupItems = (input: SetupInput) => {
  const { hasGameLinked, hasSteamLinked, steambot: bot } = input;
  // Before linking we can't know the game, so show the CS2 steps as what's
  // ahead. After linking, only Steam accounts can use them.
  const steamApplies = hasSteamLinked || !hasGameLinked;
  const items: SetupItem[] = [
    {
      key: "link",
      label: "Link a game account",
      benefit: hasGameLinked
        ? linkedLabel(input.linkedPlatforms)
        : "CS2, League, Fortnite",
      action: "Link",
      done: hasGameLinked,
      to: URLS.CONNECTED_ACCOUNTS,
    },
  ];

  if (steamApplies) {
    items.push({
      key: "sharecodes",
      label: "Share codes",
      benefit: "Fills your CS2 match history",
      action: "Add",
      done: input.hasSharecodes,
      blockedBy: hasSteamLinked ? undefined : "Link Steam first",
      to: "/add-share-codes",
    });

    items.push({
      key: "steambot",
      label: "Steam bot",
      benefit: "Get a message when your match is ready",
      action: "Add",
      done: Boolean(bot?.enrolled),
      blockedBy: steambotBlocker(input),
      to: URLS.STEAM_BOT,
    });
  }

  items.push({
    key: "autocapture",
    label: "Autocapture",
    benefit: "We clip your best plays every match",
    action: "Turn on",
    done: input.autoclip,
    blockedBy: hasGameLinked ? undefined : "Link a game first",
  });

  return {
    items,
    done: items.filter((item) => item.done).length,
    total: items.length,
  };
};

const readDismissed = () => {
  try {
    return Boolean(localStorage.getItem(DISMISS_KEY));
  } catch {
    return false;
  }
};

export const useSetupChecklist = () => {
  const { allstarUser } = useUser();
  const linked = useLinkedAccounts();
  const steambot = useSteambot();
  const [dismissed, setDismissed] = useState(readDismissed);
  const user = allstarUser?.user;

  const { items, done, total } = buildSetupItems({
    hasGameLinked:
      linked.hasSteamLinked || linked.hasRiotLinked || linked.hasEpicLinked,
    linkedPlatforms: [
      linked.hasSteamLinked && "Steam",
      linked.hasRiotLinked && "Riot",
      linked.hasEpicLinked && "Epic Games",
    ].filter((name): name is string => Boolean(name)),
    hasSteamLinked: linked.hasSteamLinked,
    // No status yet (loading or a failed request) is unknown, not ineligible.
    steambot: steambot.status && {
      loading: steambot.loading,
      eligible: steambot.status.eligible,
      enrolled: steambot.enrolled,
      capacityAvailable: steambot.status.capacityAvailable,
    },
    hasSharecodes: Boolean(user?.hasSharecodes),
    autoclip: Boolean(user?.autoclip),
  });

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, "true");
    } catch {
      // Private mode: hidden for this session only.
    }
  };

  return {
    items,
    done,
    total,
    showIndicator: !dismissed && done < total,
    dismiss,
  };
};
